/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import { tagName } from '@ember-decorators/component';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import { isColour } from '@linc-technologies/linced-common/utils/colour';
import {
  loadRubrics,
  resolveRubric,
  rubricElements,
} from '@linc-technologies/linced-common/utils/rubrics';

@tagName('')
export default class RubricDialog extends Component {
  // Services
  @service
  dates;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Required
  student = null;

  subject = null;
  onClose = null;

  // The rubric family chosen on the report template section (null for the
  // default family)
  rubricFamilyId = null;

  // Internal state
  @tracked
  rubricData = null;

  @tracked
  error = null;
  @tracked
  isLoading = false;

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this._loadRubricData();
  }

  async _loadRubricData() {
    let student = this.student;
    let subject = this.subject;
    if (!student || !subject) return;

    this.isLoading = true;
    this.error = null;
    this.rubricData = null;

    try {
      let milestones = subject.get('milestones') || [];
      let currentMilestone = getCurrentMilestone(
        milestones,
        student,
        this.dates.rolloverMonthDate,
      );
      let currentMilestoneTemplateId = currentMilestone
        ? currentMilestone.get('templateId') || currentMilestone.templateId
        : null;

      if (!currentMilestoneTemplateId) {
        this.error =
          'Could not determine the current milestone for this student.';
        this.isLoading = false;
        return;
      }

      let jurisdiction =
        this.session.school && this.session.school.jurisdiction;
      if (jurisdiction) {
        await loadRubrics(this.store, jurisdiction);
      }

      if (this.isDestroyed || this.isDestroying) return;

      let allCriteria = this.store.peekAll('reports/rubric-criteria');

      let rubric = resolveRubric(
        subject,
        student,
        this.store,
        this.dates.rolloverMonthDate,
        this.rubricFamilyId,
      );

      if (rubric) {
        let possibleGrades = subject.get('possibleGrades') || [];
        let columns = (rubric.get('columns') || []).map((col) => {
          let colNum = col.get('column');
          let grade = possibleGrades.find((g) => g.value === colNum);
          let colour = isColour(grade?.colour) ? grade.colour : '#26a69a';
          return {
            column: colNum,
            label: grade ? grade.string : col.get('label'),
            headerStyle: htmlSafe(`background-color:${colour}`),
          };
        });

        let elements = rubricElements(rubric, this.store).map((element) => {
          let elementId = element.get('id');
          let elementCriteria = allCriteria.filter(
            (c) => c.belongsTo('elementId').id() === elementId,
          );
          let cols = columns.map((colDef) => ({
            column: colDef.column,
            label: colDef.label,
            headerStyle: colDef.headerStyle,
            criteria: elementCriteria
              .filter((c) => c.get('progressColumn') === colDef.column)
              .sort(byKeys('sequence')),
          }));
          return { name: element.get('name'), columns: cols };
        });

        this.rubricData = {
          name: rubric.get('name'),
          columns,
          elements,
        };
      }
    } catch (err) {
      this.toaster.error('Failed to load rubric data');
    } finally {
      if (!this.isDestroyed && !this.isDestroying) {
        this.isLoading = false;
      }
    }
  }
}
